import {dateFormatter, timeFormatter, datetimeFormatter} from '@hconnect/uikit'
import {Box, CircularProgress} from '@material-ui/core'
import {isEmpty} from 'lodash'
import moment from 'moment-timezone'
import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {useDismissFailedOrderRequest} from '../../common/react-query/hooks/mutations/useDismissFailedOrderRequest'
import {useFailedOrderRequests} from '../../common/react-query/hooks/queries/useFailedOrderRequests'
import {useNotValidatedOrderRequests} from '../../common/react-query/hooks/queries/useNotValidatedOrderRequests'
import {ErrorBanner} from '../../Molecules/ErrorBanner'
import {InfoBanner} from '../../Molecules/InfoBanner'
import {useOptionsBase, useV2OptionsBase} from '../../OrderIntake/Options'
import {OrderIntakeMaterialOptionPayload, RequestData} from '../../OrderIntake/declarations/types'
import {useFeaturesState} from '../Features'

interface OrderRequestBannerProps {
  onReload: () => void
  customerId: string
}

export const OrderRequestBanner: React.FC<OrderRequestBannerProps> = ({onReload, customerId}) => {
  const {t} = useTranslation()
  const [isPendingOrders, setIsPendingOrders] = useState<boolean>(false)
  const [isOrderRequestsError, setIsOrderRequestsError] = useState<boolean>(false)
  const [stop, setStop] = useState<boolean>(false)
  const [isInfoBannerVisible, setIsInfoBannerVisible] = useState<boolean>(false)
  const [numberOfPendingOrders, setNumberOfPendingOrders] = useState<number>()

  const {data, error} = useNotValidatedOrderRequests(customerId, stop)

  useEffect(() => {
    if (isEmpty(data)) {
      setIsPendingOrders(false)
      setNumberOfPendingOrders(0)
      setStop(true)
      return
    }

    setIsInfoBannerVisible(true)
    setIsPendingOrders(true)
    setNumberOfPendingOrders(data?.length)
    setStop(false)

    if (error) {
      setIsPendingOrders(false)
      setIsOrderRequestsError(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error])

  const handleReloadClick = () => {
    setIsInfoBannerVisible(false)
    onReload()
  }

  return (
    <InfoBanner
      data-test-id="order-intake-info-banner"
      isVisible={isInfoBannerVisible}
      handleClick={handleReloadClick}
      isError={isOrderRequestsError}
      isPending={isPendingOrders}
      successText={t('orderIntake.ordersConfirmed')}
      errorText={t('orderIntake.errorMessage')}
      pendingText={t('orderIntake.pendingOrders', {numberOfPendingOrders})}
    />
  )
}

interface FailedOrderRequestBannerProps {
  customerId: string
  excludedRequestIds?: string[]
}

export const FailedOrderRequestBanner: React.FC<FailedOrderRequestBannerProps> = ({
  customerId,
  excludedRequestIds = []
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation()
  const {data: orderRequests, isLoading} = useFailedOrderRequests(customerId)

  const {mutate: dismissFailedOrderRequest} = useDismissFailedOrderRequest()

  // Reuse the same cached OrderIntake options query that the surrounding
  // BulkCementOrderIntakeProvider already triggers on /orders, so this adds
  // no extra network call. We only need it to look up the site timezone.
  const {getFeature} = useFeaturesState()
  const applyMaterialEnteredDescription = getFeature('OrderIntakeMaterialEnteredDescription')
  const applyContractItemDescription = getFeature('OrderIntakeContractItemDescription')
  const enableV2Options = getFeature('EnableV2Options')
  const {data: v1Options} = useOptionsBase(
    !!applyMaterialEnteredDescription,
    !!applyContractItemDescription,
    customerId
  )
  const {data: v2Options} = useV2OptionsBase(
    !!applyMaterialEnteredDescription,
    !!applyContractItemDescription,
    customerId
  )
  const orderIntakeOptions: OrderIntakeMaterialOptionPayload[] | undefined = enableV2Options
    ? v2Options
    : v1Options

  const resolveTimeZone = (requestData: RequestData): string | undefined => {
    if (!orderIntakeOptions || !requestData.shippingAddress?.siteNumber) return undefined
    return orderIntakeOptions.find(
      (o) => o.shippingAddress.siteNumber === requestData.shippingAddress.siteNumber
    )?.businessHours?.timeZone
  }

  return (
    <Box py={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
      {isLoading ? <CircularProgress /> : null}
      {orderRequests
        ?.filter(({requestId}) => !excludedRequestIds.includes(requestId))
        .map(({createdOn, modifiedOn, requestId, requestData}) => {
          const tz = resolveTimeZone(requestData)
          const earliestRaw = requestData.deliveryTime?.earliest
          const latestRaw = requestData.deliveryTime?.latest
          const earliestUtc = earliestRaw
            ? moment.utc(`${requestData.deliveryDate}T${earliestRaw}`)
            : undefined
          const latestUtc = latestRaw
            ? moment.utc(`${requestData.deliveryDate}T${latestRaw}`)
            : undefined
          const earliestLocal =
            tz && earliestUtc?.isValid() ? earliestUtc.clone().tz(tz).locale(language) : undefined
          const latestLocal =
            tz && latestUtc?.isValid() ? latestUtc.clone().tz(tz).locale(language) : undefined

          const orderDate = earliestLocal
            ? earliestLocal.format('L')
            : dateFormatter(requestData.deliveryDate, language)

          const formatFallbackTime = (raw?: string) =>
            raw ? timeFormatter(moment(raw, 'HH:mm:ss'), language) : ''
          const timeframe =
            earliestLocal && latestLocal
              ? `${earliestLocal.format('LT')} - ${latestLocal.format('LT')}`
              : `${formatFallbackTime(earliestRaw)} - ${formatFallbackTime(latestRaw)}`

          return (
            <ErrorBanner
              data-test-id="order-intake-error-banner"
              key={requestId}
              isVisible={true}
              label={t('orderIntake.failedOrderRequestBanner', {
                timestamp: datetimeFormatter(modifiedOn ?? createdOn, language),
                orderDate,
                timeframe,
                material: requestData.material?.materialDescription || '',
                qty: `${requestData.quantity}${requestData.quantityUom}`
              })}
              handleClick={() => dismissFailedOrderRequest({requestId})}
            />
          )
        })}
    </Box>
  )
}
